import {
	useState,
	useEffect,
	useRef,
	useMemo,
	useCallback,
	type ReactNode
} from 'react'
import { Eye, EyeSlash, CaretUpFill } from 'src/icons'
import PersonDetailModal from './PersonDetailModal'

// ─── TYPES ─────────────────────────────────────────────────────────────────────
export interface OrgNode {
	id: number
	name: string
	title: string
	managerId: number | null
	avatar?: string
	isYou?: boolean
	[key: string]: unknown
}

interface Theme {
	bg: string
	header: string
	headerBorder: string
	cardBg: string
	cardBgAlt: string
	cardBorder: string
	text: string
	textMuted: string
	accent: string
	accentBg: string
	sectionMgr: string
	sectionPeers: string
	sectionReports: string
	sectionBg: (depth: number) => string
	connMgr: string
	connRpt: string
	searchBg: string
	sectionBorder: string
}

type NodeIndex = Record<number, OrgNode>

interface RenderCardArgs {
	person: OrgNode
	isMe: boolean
	variant: string
	hasReports: boolean
	open: boolean
	onToggle?: () => void
	t: Theme
}

interface RenderAvatarArgs {
	person: OrgNode
	isMe: boolean
	size: number
}

interface OrgChartProps {
	nodes: OrgNode[]
	rootId?: number
	columns?: number
	accentColor?: string
	theme?: 'light' | 'dark'
	renderCard?: ((args: RenderCardArgs) => ReactNode) | null
	renderAvatar?: ((args: RenderAvatarArgs) => ReactNode) | null
}

// ─── CONSTANTS ─────────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
	['#2D3A4A', '#7EC8C8'],
	['#3B2D4A', '#C87EC8'],
	['#2D4A35', '#7EC88A'],
	['#4A3B2D', '#C8A87E'],
	['#2D3F4A', '#7EAAC8'],
	['#4A2D2D', '#C87E7E']
]

// ─── THEME ─────────────────────────────────────────────────────────────────────
function getT(theme: 'light' | 'dark', accentColor: string): Theme {
	const d = theme === 'dark'
	return {
		bg: d ? '#0B1220' : '#FFFFFF',
		header: d ? '#0D1526' : '#FFFFFF',
		headerBorder: d ? '#1A2435' : '#E8E8E8',
		cardBg: d ? '#16202E' : '#FFFFFF',
		cardBgAlt: d ? '#1E2634' : '#F8F8F8',
		cardBorder: d ? '#2A3548' : '#DEDEDE',
		text: d ? '#E8EAF0' : '#1A1A1A',
		textMuted: d ? '#5A7090' : '#888888',
		accent: accentColor,
		accentBg: d ? `${accentColor}15` : `${accentColor}10`,
		sectionMgr: d ? 'rgba(60,100,200,0.18)' : '#FFFFFF',
		sectionPeers: d ? 'rgba(40,180,100,0.14)' : '#FFFFFF',
		sectionReports: d ? 'rgba(160,80,220,0.14)' : '#F8F8F8',
		sectionBg: d
			? // dark: keep the existing 2-tone alternation (unchanged behaviour)
				(depth: number) => (depth % 2 === 0 ? '#121C2C' : '#0E1726')
			: // light: NIBBI-31 item 7 — group band darkens 5% per level, floored so
				// the deepest bands stay readable behind the black card text.
				(depth: number) => {
					const v = Math.round(255 * (1 - 0.05 * Math.min(depth, 8))) // 255→…→153 (#999999)
					const h = v.toString(16).padStart(2, '0')
					return `#${h}${h}${h}`
				},
		connMgr: d ? '#3A4560' : '#CCCCCC',
		connRpt: d ? '#2A4A3A' : '#CCCCCC',
		searchBg: d ? '#0F1A2A' : '#FFFFFF',
		sectionBorder: d ? 'none' : `1px solid #E8E8E8`
	}
}

// ─── DATA HELPERS ──────────────────────────────────────────────────────────────
function buildIndex(nodes: OrgNode[]): NodeIndex {
	const idx: NodeIndex = {}
	nodes.forEach((n) => {
		idx[n.id] = n
	})
	return idx
}
function getChildren(idx: NodeIndex, pid: number | null): OrgNode[] {
	return Object.values(idx).filter((n) => n.managerId === pid)
}
function getSiblings(idx: NodeIndex, node: OrgNode | undefined): OrgNode[] {
	if (!node) return []
	return node.managerId == null
		? Object.values(idx).filter((n) => n.managerId == null && n.id !== node.id)
		: Object.values(idx).filter((n) => n.managerId === node.managerId && n.id !== node.id)
}
// Chain of ancestors from the root (CEO) down to — but not including — nodeId.
function getChainToRoot(idx: NodeIndex, nodeId: number): OrgNode[] {
	const chain: OrgNode[] = []
	let cur = idx[nodeId]
	while (cur && cur.managerId != null) {
		const parent = idx[cur.managerId]
		if (!parent) break
		chain.unshift(parent)
		cur = parent
	}
	return chain // [root, ..., directManager]
}

// ─── HOOKS ─────────────────────────────────────────────────────────────────────
function useContainerWidth(ref: React.RefObject<HTMLElement | null>) {
	const [w, setW] = useState(400)
	useEffect(() => {
		if (!ref.current) return
		const ro = new ResizeObserver((es) => setW(es[0].contentRect.width))
		ro.observe(ref.current)
		setW(ref.current.getBoundingClientRect().width)
		return () => ro.disconnect()
	}, [])
	return w
}

// ─── SUB-COMPONENTS ────────────────────────────────────────────────────────────
export function Av({
	person,
	isMe,
	size,
	accentColor
}: {
	person: OrgNode
	isMe: boolean
	size: number
	accentColor: string
}) {
	const [bg, fg] = AVATAR_COLORS[person.id % AVATAR_COLORS.length]
	return (
		<div
			style={{
				width: size,
				height: size,
				borderRadius: 0,
				flexShrink: 0,
				background: isMe ? `linear-gradient(135deg, ${accentColor}, #F5813D)` : bg,
				color: isMe ? '#111' : fg,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				fontSize: Math.round(size * 0.28),
				fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif",
				fontWeight: 600,
				boxShadow: isMe ? `0 0 0 2px ${accentColor},0 0 12px ${accentColor}55` : 'none',
				transition: 'all 0.2s'
			}}
		>
			{person.avatar || person.name.slice(0, 2).toUpperCase()}
		</div>
	)
}

function Card({
	person,
	isMe,
	hasReports,
	open,
	onToggle,
	onSelectPerson,
	renderAvatar,
	accentColor,
	t,
	idx
}: {
	person: OrgNode
	isMe: boolean
	variant: string
	hasReports: boolean
	open: boolean
	onToggle?: () => void
	onSelectPerson: (person: OrgNode) => void
	renderAvatar?: ((args: RenderAvatarArgs) => ReactNode) | null
	accentColor: string
	t: Theme
	idx: NodeIndex
}) {
	const subordinateCount = idx
		? Object.values(idx).filter((n) => n.managerId === person.id).length
		: 0
	const bg = isMe ? t.accentBg : t.cardBg
	const border = isMe ? t.accent : t.cardBorder
	return (
		<div
			data-testid="org-card"
			data-node-id={person.id}
			data-expanded={open}
			data-is-me={isMe}
			data-has-reports={hasReports}
			onClick={() => onSelectPerson(person)}
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: 10,
				// Fixed card height (independent of photo size and title line count) so
				// every card in a row is uniformly sized — previously the card's height
				// was implicit (derived from its content), which let 1-line-title cards
				// render shorter than 2-line-title cards. 88px comfortably fits the 56px
				// photo plus the horizontal padding below.
				height: 88,
				padding: '0 10px',
				borderRadius: 4,
				border: `1px solid ${border}`,
				background: bg,
				cursor: 'pointer',
				WebkitTapHighlightColor: 'transparent',
				userSelect: 'none',
				minWidth: 0,
				width: '100%',
				overflow: 'visible',
				position: 'relative'
			}}
		>
			<div style={{ width: 56, height: 56, flexShrink: 0 }}>
				{renderAvatar ? (
					renderAvatar({ person, isMe, size: 56 })
				) : (
					<Av person={person} isMe={isMe} size={56} accentColor={accentColor} />
				)}
			</div>
			<div style={{ minWidth: 0, flex: 1 }}>
				<div
					style={{
						fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif",
						fontWeight: person.isYou ? 700 : 600,
						fontSize: 13,
						color: isMe ? t.accent : t.text,
						overflow: 'hidden',
						display: '-webkit-box',
						WebkitBoxOrient: 'vertical',
						WebkitLineClamp: 2,
						overflowWrap: 'anywhere'
					}}
				>
					{person.name}
					{person.isYou && (
						<span style={{ fontSize: 9, opacity: 0.5, fontWeight: 400, marginLeft: 4 }}>
							you
						</span>
					)}
				</div>
				<div
					style={{
						fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif",
						fontSize: 11,
						lineHeight: 1.3,
						marginTop: 2,
						color: t.textMuted,
						// Reserve exactly two lines on every card so cards in a group are
						// uniform height: short titles get a blank second line, long ones
						// clamp to two lines with an ellipsis instead of growing the card.
						height: '2.6em',
						overflow: 'hidden',
						display: '-webkit-box',
						WebkitBoxOrient: 'vertical',
						WebkitLineClamp: 2,
						overflowWrap: 'anywhere'
					}}
				>
					{person.title}
				</div>
			</div>
			{hasReports && (
				<div
					data-testid="expand-toggle"
					data-node-id={person.id}
					data-open={open}
					onClick={(e) => {
						e.stopPropagation()
						onToggle?.()
					}}
					style={{
						position: 'absolute',
						right: 12,
						bottom: 0,
						transform: 'translateY(50%)',
						display: 'flex',
						alignItems: 'center',
						gap: 5,
						background: isMe ? t.accent : t.cardBgAlt,
						border: `1px solid ${isMe ? t.accent : t.cardBorder}`,
						color: isMe ? '#FFFFFF' : t.text,
						borderRadius: 99,
						padding: '4px 12px',
						fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif",
						fontSize: 12,
						fontWeight: 600,
						lineHeight: 1,
						whiteSpace: 'nowrap',
						cursor: 'pointer',
						zIndex: 1,
						boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
						transition: 'transform 0.12s, box-shadow 0.12s'
					}}
					onMouseEnter={(e) => {
						e.currentTarget.style.transform = 'translateY(50%) scale(1.08)'
						e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.25)'
					}}
					onMouseLeave={(e) => {
						e.currentTarget.style.transform = 'translateY(50%)'
						e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.18)'
					}}
				>
					{subordinateCount}{' '}
					<CaretUpFill
						size={16}
						style={{
							display: 'block',
							transform: open ? 'rotate(180deg)' : 'none'
						}}
					/>
				</div>
			)}
		</div>
	)
}

function Grid({
	items,
	renderItem
}: {
	items: OrgNode[]
	cols: number
	renderItem: (item: OrgNode, i: number) => ReactNode
}) {
	return (
		<div
			style={{
				display: 'grid',
				gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
				columnGap: 8,
				rowGap: 18,
				minWidth: 0,
				alignItems: 'start'
			}}
		>
			{items.map((item, i) => (
				<div key={item.id ?? i} style={{ minWidth: 0 }}>
					{renderItem(item, i)}
				</div>
			))}
		</div>
	)
}

function Section({
	bg,
	border,
	t,
	action,
	children
}: {
	bg: string
	border?: string
	t: Theme
	action?: ReactNode
	children: ReactNode
}) {
	const showHeader = !!action
	return (
		<div
			style={{
				background: bg,
				borderRadius: 6,
				padding: '12px 10px',
				border: border || 'none',
				marginBottom: 8
			}}
		>
			{showHeader && (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 6,
						paddingBottom: 4,
						marginTop: -9,
						fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif",
						fontSize: 10,
						color: t.textMuted,
						letterSpacing: '0.06em',
						textTransform: 'uppercase',
						fontWeight: 600,
						userSelect: 'none'
					}}
				>
					{action && <div style={{ marginLeft: 'auto' }}>{action}</div>}
				</div>
			)}
			{children}
		</div>
	)
}

// Small inline toggle for revealing a chain manager's peer group.
function PeerToggle({
	open,
	count,
	onClick,
	t,
	nodeId
}: {
	open: boolean
	count: number
	label?: string
	onClick: () => void
	t: Theme
	nodeId: number
}) {
	return (
		<button
			data-testid="peer-toggle"
			data-node-id={nodeId}
			data-open={open}
			data-count={count}
			onClick={onClick}
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: 6,
				background: 'none',
				border: 'none',
				padding: 0,
				margin: 0,
				cursor: 'pointer',
				color: t.textMuted
			}}
		>
			{open ? <Eye size={20} /> : <EyeSlash size={20} />}
			{count > 0 && (
				<span style={{ color: t.textMuted, fontWeight: 700, fontSize: 11 }}>{count}</span>
			)}
		</button>
	)
}


// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────
export function OrgChart({
	nodes,
	rootId,
	columns = 2,
	accentColor = '#D4521A',
	theme = 'light',
	renderCard = null,
	renderAvatar = null
}: OrgChartProps) {
	const t = useMemo(() => getT(theme, accentColor), [theme, accentColor])
	const idx = useMemo(() => buildIndex(nodes), [nodes])

	// Root of the tree (CEO / managerId === null), unless a rootId is supplied.
	const treeRootId = useMemo(() => {
		if (rootId != null && idx[rootId]) return rootId
		const top = nodes.find((n) => n.managerId == null)
		return top ? top.id : nodes[0]?.id
	}, [rootId, idx, nodes])

	// "You" — the logged-in user, only when actually present in the data. Drives
	// the accent. Undefined when the current user isn't in the chart's dataset.
	const youId = useMemo(() => nodes.find((n) => n.isYou)?.id, [nodes])

	// "Me" — anchors the default open state. The logged-in user if they're in the
	// data, otherwise the tree root (CEO) so the view starts at the top rather
	// than centering on an arbitrary person.
	const meId = youId ?? treeRootId

	// The upward chain: [root ... directManager] of me.
	const chain = useMemo(() => getChainToRoot(idx, meId), [idx, meId])

	// expanded: set of node ids whose DOWNWARD subtree is open.
	// Default: my direct reports are shown (I am expanded), and the whole upward
	// chain (my manager, their manager, ... CEO) is expanded so it flows down to me.
	const [expanded, setExpanded] = useState<Set<number>>(
		() => new Set([meId, ...chain.map((c) => c.id)])
	)
	// peerOpen: set of chain-member ids whose PEER GROUP is revealed. Default: none.
	const [peerOpen, setPeerOpen] = useState<Set<number>>(() => new Set())
	// Person shown in the detail modal on card click; null when no modal is open.
	const [selectedPerson, setSelectedPerson] = useState<OrgNode | null>(null)

	const containerRef = useRef<HTMLDivElement>(null)
	const contentRef = useRef<HTMLDivElement>(null)
	const cw = useContainerWidth(containerRef)

	const cols = cw >= 900 ? Math.max(columns, 4) : cw >= 600 ? Math.max(columns, 3) : columns

	const toggleExpand = useCallback(
		(id: number) => {
			const me = idx[id]
			if (expanded.has(id)) {
				// Collapsing: clear this node AND its entire subtree from expanded/peerOpen,
				// so reopening later starts fresh — one layer, nothing pre-drilled below.
				const subtree = new Set<number>()
				const gather = (pid: number) => {
					subtree.add(pid)
					getChildren(idx, pid).forEach((c) => gather(c.id))
				}
				gather(id)
				setExpanded((prev) => {
					const next = new Set(prev)
					subtree.forEach((s) => next.delete(s))
					return next
				})
				setPeerOpen((prev) => {
					const next = new Set(prev)
					subtree.forEach((s) => next.delete(s))
					return next
				})
				return
			}
			// Expanding: collapse any already-expanded sibling (same manager), so only
			// one member of a peer group is expanded at a time.
			setExpanded((prev) => {
				const next = new Set(prev)
				if (me) {
					Object.values(idx).forEach((n) => {
						if (n.id !== id && n.managerId === me.managerId && next.has(n.id))
							next.delete(n.id)
					})
				}
				next.add(id)
				return next
			})
		},
		[idx, expanded]
	)

	// Order a peer group so the expanded member (if any) sits last — its caret
	// then points down into the report group rendered directly below it.
	const expandedLast = useCallback(
		(arr: OrgNode[]) => {
			const open = arr.filter((p) => expanded.has(p.id))
			const closed = arr.filter((p) => !expanded.has(p.id))
			return [...closed, ...open]
		},
		[expanded]
	)

	const togglePeers = useCallback((id: number) => {
		setPeerOpen((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}, [])

	function mkCard(person: OrgNode, opts: { variant?: string; onToggle?: () => void } = {}) {
		const { variant = 'report', onToggle } = opts
		const hasReports = getChildren(idx, person.id).length > 0
		const open = expanded.has(person.id)
		// Accent only the real logged-in user; never the fallback anchor.
		const isMe = youId != null && person.id === youId
		if (renderCard) return renderCard({ person, isMe, variant, hasReports, open, onToggle, t })
		return (
			<Card
				person={person}
				isMe={isMe}
				variant={variant}
				hasReports={hasReports}
				open={open}
				onToggle={onToggle}
				onSelectPerson={setSelectedPerson}
				renderAvatar={renderAvatar}
				accentColor={accentColor}
				t={t}
				idx={idx}
			/>
		)
	}

	// Render a row of report cards, expanded member last.
	function renderReportGrid(kids: OrgNode[]) {
		return (
			<Grid
				items={expandedLast(kids)}
				cols={cols}
				renderItem={(p) => {
					const kidHasReports = getChildren(idx, p.id).length > 0
					return mkCard(p, {
						variant: 'report',
						onToggle: kidHasReports ? () => toggleExpand(p.id) : undefined
					})
				}}
			/>
		)
	}

	const meNode = idx[meId]

	// Build the followed path of node ids from the root down to the deepest
	// expanded node. At each layer only one node is expanded (enforced by
	// toggleExpand), so the path is unambiguous. Default path = my chain to me,
	// then whatever I've expanded below. Expanding a peer of any node switches
	// the path to follow that peer instead.
	function buildFollowedPath() {
		// Find the topmost ancestor (root of the whole tree the focus belongs to).
		let top: OrgNode | undefined = meNode
		while (top && top.managerId != null && idx[top.managerId]) top = idx[top.managerId]
		const path: number[] = []
		let cur: OrgNode | null = top ?? null
		while (cur) {
			path.push(cur.id)
			// descend to the expanded child of cur, if any
			const kids = getChildren(idx, cur.id)
			const nextOpen = kids.find((k) => expanded.has(k.id))
			cur = nextOpen || null
		}
		return path // [rootId, ..., deepestExpandedId]
	}

	// Render one layer: the node (with its peer group if peers are toggled on for
	// it) as a Section, expanded member last. variant kept for renderCard context.
	function renderLayer(nodeId: number, pos: number) {
		const node = idx[nodeId]
		if (!node) return null
		const peers = getSiblings(idx, node)
		const peersShown = peerOpen.has(nodeId)
		const groupMembers = peersShown ? [node, ...peers] : [node]
		return (
			<div key={`layer-${nodeId}`}>
				<Section
					bg={t.sectionBg(pos)}
					border={t.sectionBorder}
					t={t}
					action={
						peers.length > 0 ? (
							<PeerToggle
								open={peersShown}
								count={peers.length}
								label="peers"
								onClick={() => togglePeers(nodeId)}
								t={t}
								nodeId={nodeId}
							/>
						) : undefined
					}
				>
					<Grid
						items={expandedLast(groupMembers)}
						cols={cols}
						renderItem={(p) =>
							mkCard(p, {
								variant: p.id === meId ? 'peer' : 'manager',
								onToggle:
									getChildren(idx, p.id).length > 0
										? () => toggleExpand(p.id)
										: undefined
							})
						}
					/>
				</Section>
			</div>
		)
	}

	// Render the report group directly below a node that has its subs expanded.
	function renderReportLayerFor(nodeId: number, pos: number) {
		const kids = getChildren(idx, nodeId)
		if (kids.length === 0) return null
		// The child (if any) that continues the followed path.
		const onPath = kids.filter(
			(k) => getChildren(idx, k.id).length > 0 && expanded.has(k.id)
		)
		const following = onPath.length > 0 // we've drilled through one child
		const peersShown = peerOpen.has(nodeId) // user asked to reveal siblings here
		// Default: if following a child, show only that child (single-line chain);
		// otherwise (leaf — no child expanded) show all reports. Revealing peers
		// shows every sibling regardless.
		const shownKids = following && !peersShown ? onPath : kids
		// Offer the peers toggle only when there are hidden siblings to reveal.
		const canToggle = following && kids.length > onPath.length
		return (
			<Section
				key={`rpt-${nodeId}`}
				bg={t.sectionBg(pos)}
				border={t.sectionBorder}
				t={t}
				action={
					canToggle ? (
						<PeerToggle
							open={peersShown}
							count={kids.length - onPath.length}
							label="peers"
							onClick={() => togglePeers(nodeId)}
							t={t}
							nodeId={nodeId}
						/>
					) : undefined
				}
			>
				{renderReportGrid(shownKids)}
			</Section>
		)
	}

	// The full followed path, rendered top→down: the root's own group, then each
	// expanded node's reports group (which contains the next followed node).
	function renderFollowedPath() {
		const path = buildFollowedPath()
		const out: ReactNode[] = []
		let pos = 0
		// Root layer (the top node + its peers, though the true root rarely has any).
		out.push(renderLayer(path[0], pos++))
		// Walk down the path. A node's reports group renders only while every node
		// above it (and itself) is expanded. The moment we hit a collapsed node we
		// stop — everything "below" a collapsed node is, by definition, hidden.
		for (const nodeId of path) {
			if (!expanded.has(nodeId)) break
			if (getChildren(idx, nodeId).length > 0)
				out.push(renderReportLayerFor(nodeId, pos++))
		}
		return out
	}

	if (!meNode)
		return (
			<div data-testid="org-chart-error" style={{ color: 'red', padding: 20 }}>
				Node not found: {meId}
			</div>
		)

	return (
		<div
			ref={containerRef}
			style={{
				background: t.bg,
				fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif",
				overflowX: 'hidden',
				position: 'relative'
			}}
		>
			{/* Scoped to the chart content only. A bare `*` reset here is not scoped to
			    this component — it leaks document-wide and shrinks any element sized as
			    width-plus-padding (e.g. PersonCard's contact icons). */}
			<style>{`
        .org-chart-content, .org-chart-content *, .org-chart-content *::before, .org-chart-content *::after { box-sizing: border-box; }
      `}</style>

			{/* Chart content */}
			<div
				ref={contentRef}
				className="org-chart-content"
				style={{ overflowX: 'hidden', padding: '12px 0' }}
			>
				{/* ── FOLLOWED PATH: root → ... → deepest expanded, treated uniformly ── */}
				{renderFollowedPath()}

				<div style={{ height: 32 }} />
			</div>

			{selectedPerson && (
				<PersonDetailModal person={selectedPerson} onClose={() => setSelectedPerson(null)} />
			)}
		</div>
	)
}

export default OrgChart
